/**
 * useProductSliderData - Composition hook for ProductSlider products
 *
 * Determines which query to use (category vs SKUs) based on slider config
 * and returns the appropriate products and loading state.
 */
import { useMemo } from 'react'

import { useProductsByCategory } from './useProductsByCategory'
import { useProductsBySkus } from './useProductsBySkus'

import type { Product } from '../../../UiBlocks'
import type { HomepageProductSlider } from '../../types'

export interface UseProductSliderDataOptions {
  /** Slider configuration from homepage config */
  slider: HomepageProductSlider
}

export interface UseProductSliderDataResult {
  /** Transformed products for ProductCard */
  products: Product[]
  /** Loading state from the active query */
  isLoading: boolean
  /** Error state from the active query */
  isError: boolean
  /** Error object if any */
  error: Error | null
}

/**
 * Fetch products for a ProductSlider based on its configuration
 */
export const useProductSliderData = ({
  slider,
}: UseProductSliderDataOptions): UseProductSliderDataResult => {
  const skuList = useMemo(() => {
    if (slider.source_type !== 'skus' || !slider.source_value) return []
    return slider.source_value.split(',').map(sku => sku.trim()).filter(Boolean)
  }, [slider.source_type, slider.source_value])

  const categoryQuery = useProductsByCategory({
    categoryId: slider.source_value ?? '',
    pageSize: slider.products_count,
    enabled: slider.source_type === 'category' && !!slider.source_value,
  })

  const skusQuery = useProductsBySkus({
    skus: skuList,
    pageSize: slider.products_count,
    enabled: slider.source_type === 'skus' && skuList.length > 0,
  })

  return slider.source_type === 'category' ? categoryQuery : skusQuery
}
